import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldAlert, Upload, X, FileText, CheckCircle2, AlertTriangle, ArrowLeft,
} from 'lucide-react';
import toast from '../utils/toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import Modal from '../components/UI/Modal';
import ConfirmModal from '../components/UI/ConfirmModal';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import { whistleblowingAPI } from '../services/api';

// Kept in sync with VIOLATION_TYPES in staff_core/whistleblowing_views.py
const VIOLATION_TYPES = [
  'Bribery, Corruption, Fraudulent Account, Embezzlement and Anti-trust / Competition',
  'Theft and Misuse of Company Assets',
  'Material Breach of Group Policy',
  'Retaliatory Actions and Others',
  'Violation of Laws and Regulations',
];

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB per file (HR portal standard)
const MAX_ATTACHMENTS = 5;

const CERTIFICATION_TEXT =
  'I hereby certify that the above information as provided by me is true, complete and ' +
  'accurate to the best of my knowledge. I further understand that any wilful act on my ' +
  'part in withholding information or making false statements in this report is in itself ' +
  'sufficient ground for disciplinary action.';

const initialForm = {
  reporter_name: '',
  reporter_email: '',
  primary_personnel_name: '',
  violation_type: '',
  incident_details: '',
  company_location_department: '',
  secondary_personnel: '',
  how_known: '',
};

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-sm text-red-600" role="alert">{message}</p> : null;

const RequiredMark = () => <span className="text-red-600" aria-hidden="true"> *</span>;

const SelfServiceWhistleblowing = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [attentionAcknowledged, setAttentionAcknowledged] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [certified, setCertified] = useState(false);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [success, setSuccess] = useState(null); // { reference_id }

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  const isDirty = useMemo(() => {
    if (certified || files.length > 0) return true;
    return Object.values(form).some((v) => String(v || '').trim() !== '');
  }, [form, certified, files]);

  const requiredComplete = useMemo(() => (
    form.primary_personnel_name.trim() &&
    form.violation_type &&
    form.incident_details.trim() &&
    form.company_location_department.trim() &&
    form.secondary_personnel.trim() &&
    form.how_known.trim() &&
    certified
  ), [form, certified]);

  const validate = () => {
    const next = {};
    if (!form.primary_personnel_name.trim()) next.primary_personnel_name = 'This field is required.';
    if (!form.violation_type) next.violation_type = 'Please select a type of violation.';
    if (!form.incident_details.trim()) next.incident_details = 'This field is required.';
    if (!form.company_location_department.trim()) next.company_location_department = 'This field is required.';
    if (!form.secondary_personnel.trim()) next.secondary_personnel = 'This field is required.';
    if (!form.how_known.trim()) next.how_known = 'This field is required.';
    if (!certified) next.certification = 'You must certify the information before submitting.';
    if (form.reporter_email.trim()) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.reporter_email.trim());
      if (!ok) next.reporter_email = 'Please enter a valid email address.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file after removal
    if (!picked.length) return;

    const accepted = [];
    for (const f of picked) {
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`"${f.name}" is not an allowed file type.`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${f.name}" exceeds the 5MB limit.`);
        continue;
      }
      accepted.push(f);
    }

    setFiles((prev) => {
      const combined = [...prev];
      for (const f of accepted) {
        if (combined.length >= MAX_ATTACHMENTS) {
          toast.error(`You can attach at most ${MAX_ATTACHMENTS} files.`);
          break;
        }
        // De-dupe by name+size
        if (!combined.some((c) => c.name === f.name && c.size === f.size)) {
          combined.push(f);
        }
      }
      return combined;
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error('Please complete the required fields.');
      return;
    }

    const fd = new FormData();
    fd.append('reporter_name', form.reporter_name.trim());
    fd.append('reporter_email', form.reporter_email.trim());
    fd.append('primary_personnel_name', form.primary_personnel_name.trim());
    fd.append('violation_type', form.violation_type);
    fd.append('incident_details', form.incident_details.trim());
    fd.append('company_location_department', form.company_location_department.trim());
    fd.append('secondary_personnel', form.secondary_personnel.trim());
    fd.append('how_known', form.how_known.trim());
    fd.append('certification', 'true');
    files.forEach((f) => fd.append('attachments', f));

    setSubmitting(true);
    try {
      const res = await whistleblowingAPI.submit(fd);
      setSuccess({ reference_id: res?.data?.reference_id });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        setErrors(apiErrors);
        toast.error('Please correct the highlighted fields.');
      } else if (err?.response?.status === 429) {
        toast.error('Too many submissions. Please try again later.');
      } else {
        const msg = err?.response?.data?.error
          || 'We could not submit your report. Your report was not stored — please try again.';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      navigate('/self-service');
    }
  };

  // ---- Success view ----
  if (success) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <div className="card">
            <div className="card-content text-center py-10">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-subsection-header mb-2">Report submitted</h1>
              <p className="text-body mb-4">
                Your report has been submitted successfully. Thank you for raising your concern.
              </p>
              {success.reference_id && (
                <p className="text-sm text-gray-500 mb-6">
                  Reference: <span className="font-mono font-semibold text-gray-700">{success.reference_id}</span>
                </p>
              )}
              <p className="text-xs text-gray-400 mb-8 max-w-md mx-auto">
                No copy of this submission is stored on the HR portal. Your report has been
                routed directly to the authorised third-party assessor.
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Mandatory ATTENTION modal — must be acknowledged before the form is usable */}
      <Modal
        isOpen={!attentionAcknowledged}
        onClose={() => {}}
        closable={false}
        theme="employee"
        size="lg"
        title="ATTENTION!"
        footer={
          <div className="flex items-center justify-between w-full gap-3">
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-700"
              onClick={() => navigate('/self-service')}
            >
              Return without reporting
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setAttentionAcknowledged(true)}
            >
              Done
            </button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
          <p>
            Winner Engineering Pte Ltd, being a member of Exeo Group, abides by strict
            compliance of various regulations, law and ethics.
          </p>
          <p>
            This channel is provided for anonymous submission of any wrongdoings that crosses
            boundaries of the following categories:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Bribery &amp; corruption, fraudulent account, embezzlement and anti-trust / competition</li>
            <li>Theft and misuse of company assets</li>
            <li>Retaliatory actions, and others</li>
            <li>Material breaches of Group policy</li>
            <li>Violations of laws and regulations</li>
          </ul>
          <p>
            All reported concerns are treated seriously and will direct resources towards
            investigation and monitoring of the cases. Exeo Group does not wish for any employee
            to misuse the channel for personal gains, mere expressions of grievances and frivolous
            and malicious whistleblowing. If the results of the investigation show that the Employee
            has acted maliciously in reporting their concern, they will be subject to disciplinary
            action up to and including termination of employment. Therefore the informant is
            reminded to carefully assess the information submitted are legitimate concerns.
          </p>
          <p>
            For genuine complaints, Exeo Group will not tolerate the harassment or victimization of
            anyone who reports a concern in good faith. Anyone who engaged in retaliation against
            someone who reports a concern will face disciplinary action, up to and including
            termination, regardless of that person’s position or stature within Exeo Group.
          </p>
          <p className="font-medium text-gray-900">
            No information of your submission will be stored on the app’s platform and all
            information will only be directed to the Exeo Group authorised third party assessor.
          </p>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showCancelConfirm}
        title="Discard this report?"
        message="You have entered information that has not been submitted. If you leave now, it will be discarded."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        theme="employee"
        onConfirm={() => { setShowCancelConfirm(false); navigate('/self-service'); }}
        onCancel={() => setShowCancelConfirm(false)}
      />

      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-blue-800" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 min-w-0">Anonymous Misconduct Reporting Form</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Report concerns confidentially. Your identity is not collected or stored.
          </p>
        </div>

        {/* Anonymity notice */}
        <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
          <AlertTriangle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900 max-w-4xl">
            This submission is anonymous. No information is stored on the HR portal and your
            report is routed only to the authorised third-party assessor. Providing your name and
            email is entirely optional — only do so if you wish to be contacted.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="card">
            <div className="card-content">
              {/* xl: the account sits beside where / who else / how. Narrower screens keep this order. */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 xl:col-span-2">
                <div>
                  <label htmlFor="reporter_name" className="form-label">Your Name</label>
                  <input
                    id="reporter_name"
                    type="text"
                    className="input w-full"
                    placeholder="Optional"
                    autoComplete="off"
                    value={form.reporter_name}
                    onChange={(e) => setField('reporter_name', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="reporter_email" className="form-label">Your E-mail Address</label>
                  <input
                    id="reporter_email"
                    type="email"
                    className="input w-full"
                    placeholder="Optional"
                    autoComplete="off"
                    value={form.reporter_email}
                    onChange={(e) => setField('reporter_email', e.target.value)}
                    aria-invalid={!!errors.reporter_email}
                  />
                  <FieldError message={errors.reporter_email} />
                </div>
              </div>

              {/* Primary personnel — full width so the subject line stays readable */}
              <div className="xl:col-span-2">
                <label htmlFor="primary_personnel_name" className="form-label">
                  Name of Primary Personnel Regarding Misconduct<RequiredMark />
                </label>
                <input
                  id="primary_personnel_name"
                  type="text"
                  className="input w-full"
                  value={form.primary_personnel_name}
                  onChange={(e) => setField('primary_personnel_name', e.target.value)}
                  aria-invalid={!!errors.primary_personnel_name}
                />
                <FieldError message={errors.primary_personnel_name} />
              </div>

              {/* Violation type — full width; the option labels are long sentences */}
              <div className="xl:col-span-2">
                <label className="form-label">
                  Type of Violation<RequiredMark />
                </label>
                <SearchableCombobox
                  variant="form"
                  theme="employee"
                  items={VIOLATION_TYPES}
                  value={form.violation_type}
                  onChange={(val) => setField('violation_type', val)}
                  valueFn={(item) => item}
                  displayFn={(item) => item}
                  placeholder="Select a type of violation..."
                  searchPlaceholder="Search violation types..."
                  className="w-full"
                />
                <FieldError message={errors.violation_type} />
              </div>

              {/* Account — grows to sit beside the three context questions */}
              <div className="xl:row-span-3 flex flex-col xl:h-full">
                <label htmlFor="incident_details" className="form-label">
                  Details of When and What Happened<RequiredMark />
                </label>
                <textarea
                  id="incident_details"
                  rows={8}
                  className="textarea w-full min-h-[12rem] xl:h-auto xl:flex-1"
                  value={form.incident_details}
                  onChange={(e) => setField('incident_details', e.target.value)}
                  aria-invalid={!!errors.incident_details}
                />
                <FieldError message={errors.incident_details} />
              </div>

              {/* Company / location / department */}
              <div>
                <label htmlFor="company_location_department" className="form-label">
                  Detail of Company (if not in presiding company), Location or Department It Occurred<RequiredMark />
                </label>
                <textarea
                  id="company_location_department"
                  rows={3}
                  className="textarea w-full"
                  value={form.company_location_department}
                  onChange={(e) => setField('company_location_department', e.target.value)}
                  aria-invalid={!!errors.company_location_department}
                />
                <FieldError message={errors.company_location_department} />
              </div>

              {/* Secondary personnel */}
              <div>
                <label htmlFor="secondary_personnel" className="form-label">
                  Name of Secondary Personnel Whom are Aware of the Misconduct or Whom You Have Spoken the Matter to<RequiredMark />
                </label>
                <textarea
                  id="secondary_personnel"
                  rows={3}
                  className="textarea w-full"
                  value={form.secondary_personnel}
                  onChange={(e) => setField('secondary_personnel', e.target.value)}
                  aria-invalid={!!errors.secondary_personnel}
                />
                <FieldError message={errors.secondary_personnel} />
              </div>

              {/* How known */}
              <div>
                <label htmlFor="how_known" className="form-label">
                  How You Got to Know of the Incident<RequiredMark />
                </label>
                <textarea
                  id="how_known"
                  rows={3}
                  className="textarea w-full"
                  value={form.how_known}
                  onChange={(e) => setField('how_known', e.target.value)}
                  aria-invalid={!!errors.how_known}
                />
                <FieldError message={errors.how_known} />
              </div>

              <div className="xl:col-span-2">
                <label className="form-label">Attach Supporting Documents</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_EXTENSIONS.join(',')}
                  onChange={handleFilePick}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border border-dashed border-gray-300 rounded-xl py-4 px-3 flex flex-col items-center gap-1.5 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <Upload className="w-6 h-6 text-blue-600" />
                  <span className="font-medium text-sm">Choose files</span>
                  <span className="text-xs text-gray-400">
                    PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG — up to 5MB each (max {MAX_ATTACHMENTS})
                  </span>
                </button>

                {files.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {files.map((f, idx) => (
                      <li
                        key={`${f.name}-${idx}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span className="truncate text-sm text-gray-700">{f.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            ({(f.size / 1024).toFixed(0)} KB)
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          className="icon-button flex-shrink-0"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="xl:col-span-2 rounded-xl bg-gray-50 border border-gray-200 p-4">
                <p className="text-sm text-gray-600 italic mb-3">“{CERTIFICATION_TEXT}”</p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    checked={certified}
                    onChange={(e) => {
                      setCertified(e.target.checked);
                      setErrors((prev) => (prev.certification ? { ...prev, certification: undefined } : prev));
                    }}
                  />
                  <span className="text-sm text-gray-800">
                    I agree and certify that the information provided is true, complete and accurate.
                    <RequiredMark />
                  </span>
                </label>
                <FieldError message={errors.certification} />
              </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <button type="button" className="btn btn-outline" onClick={handleCancel} disabled={submitting}>
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!requiredComplete || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceWhistleblowing;
